/**
 * DialogNuevoPago — Modal para registrar un pago.
 *
 * Permite:
 *  - Seleccionar cargo a pagar (o pago general sin cargo)
 *  - Método: efectivo, tarjeta, transferencia, etc.
 *  - Monto (autocompleta con saldo del cargo seleccionado)
 *  - Referencia (#auth, últimos 4 dígitos, #transferencia)
 *  - Notas
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Dialog, Slide,
  FormControl, InputLabel, Select, MenuItem, Chip, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Close as CloseIcon,
  AttachMoney as CashIcon,
  CreditCard as CardIcon,
  AccountBalance as TransferIcon,
  Receipt as CheckIcon,
} from '@mui/icons-material';

const API_URL = '/api';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, background: '#f8fafc', fontSize: 13.5,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#1e3a5f', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12.5, fontWeight: 600, color: '#475569' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' },
};

const METODOS = [
  { id: 'efectivo',         label: 'Efectivo',     icon: <CashIcon /> },
  { id: 'tarjeta_debito',   label: 'Débito',       icon: <CardIcon /> },
  { id: 'tarjeta_credito',  label: 'Crédito',      icon: <CardIcon /> },
  { id: 'transferencia',    label: 'Transferencia',icon: <TransferIcon /> },
  { id: 'cheque',           label: 'Cheque',       icon: <CheckIcon /> },
];

function fmtMoney(num, moneda = 'MXN') {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda }).format(Number(num || 0));
}

export default function DialogNuevoPago({
  open, onClose, paciente, userId,
  cargoPreseleccionado = null,
  cargosPendientes = [],
  onCreado,
}) {
  const [cargoId, setCargoId] = useState('');
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState('efectivo');
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');
  const [moneda, setMoneda] = useState('MXN');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  // Reset al abrir
  useEffect(() => {
    if (!open) return;
    setError('');
    setReferencia('');
    setNotas('');
    setMetodo('efectivo');

    if (cargoPreseleccionado) {
      setCargoId(String(cargoPreseleccionado.id));
      setMonto(String(cargoPreseleccionado.saldo));
      setMoneda(cargoPreseleccionado.moneda || 'MXN');
    } else {
      // Si hay cargos pendientes, preseleccionar el más reciente
      if (cargosPendientes.length > 0) {
        const c = cargosPendientes[0];
        setCargoId(String(c.id));
        setMonto(String(c.saldo));
        setMoneda(c.moneda || 'MXN');
      } else {
        setCargoId('');
        setMonto('');
        setMoneda('MXN');
      }
    }
  }, [open, cargoPreseleccionado, cargosPendientes]);

  // Si cambia el cargo seleccionado, ajustar monto
  const handleCargoChange = (id) => {
    setCargoId(id);
    if (id === '') {
      setMonto('');
      return;
    }
    const c = cargosPendientes.find(x => String(x.id) === id);
    if (c) {
      setMonto(String(c.saldo));
      setMoneda(c.moneda || 'MXN');
    }
  };

  const cargoSel = cargosPendientes.find(c => String(c.id) === cargoId);
  const montoNum = parseFloat(monto) || 0;
  const valido = montoNum > 0
    && (cargoId === '' || (cargoSel && montoNum <= Number(cargoSel.saldo)));

  const handleGuardar = async () => {
    if (!valido) return;
    setGuardando(true);
    setError('');
    try {
      const body = {
        paciente_id: paciente.id,
        cargo_id: cargoId ? parseInt(cargoId) : null,
        monto: montoNum,
        moneda,
        metodo,
        referencia: referencia.trim() || null,
        notas: notas.trim() || null,
      };
      const res = await fetch(`${API_URL}/emr/finanzas/pagos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt.substring(0, 250));
      }
      onCreado?.();
    } catch (e) {
      setError(e.message || 'Error al registrar pago');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !guardando && onClose()}
      TransitionComponent={SlideUp}
      maxWidth="xs" fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3, background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 60px rgba(15,23,42,0.25)', overflow: 'hidden',
        },
      }}
    >
      {/* Header slim — verde para diferenciar de cargos */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        py: 1, pl: 2, pr: 1,
        background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)', color: '#fff',
      }}>
        <PaymentIcon sx={{ fontSize: 18, color: '#fef3c7' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.2, lineHeight: 1.1 }}>
            Registrar pago
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.85 }}>
            {paciente?.nombre_completo}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={guardando}
          sx={{ color: 'rgba(255,255,255,0.85)', p: 0.4, '&:hover': { background: 'rgba(255,255,255,0.12)' } }}>
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '74vh', overflowY: 'auto' }}>

        {/* Selector de cargo a pagar */}
        <FormControl size="small" sx={inputStyle}>
          <InputLabel shrink>Aplicar a cargo</InputLabel>
          <Select
            label="Aplicar a cargo" notched
            value={cargoId}
            onChange={(e) => handleCargoChange(e.target.value)}
          >
            <MenuItem value="">
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Pago sin cargo (anticipo)</Typography>
                <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>Saldo a favor del paciente</Typography>
              </Box>
            </MenuItem>
            {cargosPendientes.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.descripcion}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                      {new Date(c.fecha_emision).toLocaleDateString('es', { day: '2-digit', month: 'short' })}
                      {c.estado === 'parcial' && ' · pago parcial'}
                    </Typography>
                  </Box>
                  <Chip
                    label={`Saldo ${fmtMoney(c.saldo, c.moneda)}`}
                    size="small"
                    sx={{ height: 22, fontSize: 11, fontWeight: 700, background: '#fef2f2', color: '#dc2626' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Método de pago - chips visuales */}
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.6 }}>
            Método de pago
          </Typography>
          <ToggleButtonGroup
            value={metodo} exclusive
            onChange={(_, v) => v && setMetodo(v)}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}
          >
            {METODOS.map(m => (
              <ToggleButton
                key={m.id} value={m.id}
                sx={{
                  textTransform: 'none', fontSize: 11.5, fontWeight: 700,
                  border: '1px solid #e2e8f0 !important', borderRadius: '10px !important',
                  px: 1.2, py: 0.5, gap: 0.5,
                  color: '#475569',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
                    color: '#fff',
                    borderColor: 'transparent !important',
                    '&:hover': { background: 'linear-gradient(135deg, #14532d 0%, #15803d 100%)' },
                  },
                }}
              >
                {React.cloneElement(m.icon, { sx: { fontSize: 14 } })}
                {m.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Monto */}
        <Box sx={{ display: 'flex', gap: 1.2 }}>
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            type="number" label="Monto" required
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            error={cargoSel && montoNum > Number(cargoSel.saldo)}
            helperText={
              cargoSel
                ? (montoNum > Number(cargoSel.saldo)
                    ? `Excede el saldo (${fmtMoney(cargoSel.saldo, moneda)})`
                    : `Saldo del cargo: ${fmtMoney(cargoSel.saldo, moneda)}`)
                : ''
            }
          />
          <FormControl size="small" sx={{ ...inputStyle, minWidth: 90 }}>
            <InputLabel shrink>Moneda</InputLabel>
            <Select label="Moneda" notched value={moneda} onChange={(e) => setMoneda(e.target.value)}>
              <MenuItem value="MXN">MXN</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="COP">COP</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Referencia + notas */}
        <TextField
          sx={inputStyle} size="small"
          label="Referencia (opcional)"
          placeholder="# autorización, últimos 4 de tarjeta, # transferencia..."
          value={referencia}
          onChange={(e) => setReferencia(e.target.value)}
        />

        <TextField
          sx={inputStyle} size="small"
          label="Notas (opcional)"
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
        />

        {error && (
          <Alert severity="error" sx={{ py: 0.4, fontSize: 12 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
        <Button
          fullWidth onClick={onClose} disabled={guardando}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: 12.5, borderRadius: 2, py: 1,
            color: '#475569', background: '#f1f5f9',
            '&:hover': { background: '#e2e8f0' },
          }}
        >
          Cancelar
        </Button>
        <Button
          fullWidth variant="contained"
          onClick={handleGuardar} disabled={!valido || guardando}
          startIcon={guardando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 12.5, borderRadius: 2, py: 1,
            background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
            boxShadow: '0 4px 12px rgba(21,128,61,0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #14532d 0%, #15803d 100%)' },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
          }}
        >
          {guardando ? 'Registrando...' : `Registrar ${fmtMoney(montoNum, moneda)}`}
        </Button>
      </Box>
    </Dialog>
  );
}
